import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { yaml } from '@codemirror/lang-yaml'
import { oneDark } from '@codemirror/theme-one-dark'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { keymap } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { api } from '../api'
import { APP_DEFAULTS } from '../defaults'
import SearchInput from './common/SearchInput'
import HighlightText from './common/HighlightText'
import { filterByText } from '../utils/filterByText'
import { restoreSelectionKeys } from '../utils/selectionKeys'
import { copyToClipboard } from '../utils/clipboard'
import GcsExportDialog from './common/GcsExportDialog'
import GcsImportDialog from './common/GcsImportDialog'
import { downloadText, ensureExtension } from '../utils/download'
import { suggestDuplicateName } from '../utils/duplicateName'
import DuplicateDialog from './common/DuplicateDialog'
import SvRecommendDialog from './common/SvRecommendDialog'
import TokenBadge from './common/TokenBadge'
import { useRunningJobs } from '../hooks/useRunningJobs'

// KBNL-48: assemble 응답에 비치명적 경고가 있으면 노란색 토스트로 노출.
// 가장 흔한 케이스: 정의되지 않은 SV 참조 (LLM hallucinate / 외부 SV 참조 등).
// 조립 자체는 성공했으니 사용자가 결과를 그대로 쓰거나 YAML 을 수정해 다시 조립할 수 있다.
function showAssembleWarnings(res, showToast) {
  const warnings = res?.warnings || []
  if (!warnings.length || !showToast) return
  // 메시지 다발이면 합쳐서 한 토스트로 노출 — 토스트 폭주 방지.
  // KBNL-137: 경고가 문자열(generate) 또는 {message}(assemble) 두 형태 모두 수용.
  const lines = warnings.map(w => (typeof w === 'string' ? w : (w.message || JSON.stringify(w))))
  const head = warnings.length === 1
    ? '조합 경고'
    : `조합 경고 (${warnings.length}건)`
  showToast('warning', `${head}: ${lines.join(' | ')}`)
}

/**
 * Composite View 뷰어
 *
 * Semantic Views와 기존 Composite Views를 모두 선택하여 결합할 수 있다.
 * Composite View끼리도 묶어서 결합하는 기능을 지원한다.
 *
 * 레이아웃:
 *   ┌──────────────────┬──────────────────────┬──────────────────────┐
 *   │ Saved Composites  │                      │                      │
 *   │ ──────────────── │  Composite View YAML  │   조립 결과           │
 *   │ Composite Views   │  (CodeMirror)         │  (카탈로그/컨텍스트)   │
 *   │ (체크 - 결합용)    │                      │                      │
 *   │ ──────────────── │                      │                      │
 *   │ Semantic Views    │                      │                      │
 *   │ (체크 - 선택용)    │                      │                      │
 *   └──────────────────┴──────────────────────┴──────────────────────┘
 */
/* ── 협업 모드 복합 키 (리뷰 #1) ──────────────────────────────────────────
 *
 * 같은 이름의 SV/CV 가 다른 owner_user_id 로 여러 벌 존재할 수 있는 협업 모드
 * 에서, 선택 state 키로 v.name 만 쓰면:
 *   - React key 충돌 (같은 key 두 list item)
 *   - 체크박스 토글 시 둘 다 영향 / 하나만 표시
 *   - owner mapping 이 Map.set 으로 마지막 owner 만 살아남음 → 잘못된 owner 의
 *     YAML 이 백엔드로 전송됨
 *
 * 해결: selectedMetrics / selectedCompositeRefs 의 element 를 'owner::name' 형태
 * 복합 키로 전환. 백엔드 payload 빌드 시점에 splitKey 로 분리해서 기존 형식
 * (selected_views: [name], selected_views_meta: [{name, owner_user_id}]) 그대로
 * 전송 — API 계약은 변하지 않음.
 *
 * owner_user_id 가 비어 있는 단독 사용자 환경은 키가 '::name' — 동작 동일.
 */
const itemOwner = (it) => it.owner_user_id || it.user_id || ''
const itemKey = (it) => `${itemOwner(it)}::${it.name}`
function splitItemKey(key) {
  const idx = key.indexOf('::')
  if (idx < 0) return { owner_user_id: '', name: key }
  return { owner_user_id: key.slice(0, idx), name: key.slice(idx + 2) }
}

/* ── YAML에서 semantic_view 이름을 추출 ──────────────────────────────── */
function extractViewNamesFromYaml(yamlStr) {
  const names = []
  const blocks = yamlStr.split(/semantic_view\s*:/)
  for (const block of blocks.slice(1)) {
    const m = block.match(/^\s*name:\s*(.+)$/m)
    if (m) names.push(m[1].trim().replace(/^["']|["']$/g, ''))
  }
  return names
}

export default function CompositeViewer({ showToast, storageSettings, editorTheme, settings }) {
  // ── Semantic View 목록 (소스 뷰 선택용) ──────────────────────────────
  const [savedViews, setSavedViews] = useState([])
  const [selectedMetrics, setSelectedMetrics] = useState(new Set())
  const [loadingViews, setLoadingViews] = useState(false)

  // ── 저장된 Composite View 목록 ──────────────────────────────────────
  const [savedComposites, setSavedComposites] = useState([])
  const [selectedComposite, setSelectedComposite] = useState(null)
  const [selectedCompositeOwner, setSelectedCompositeOwner] = useState('')
  const [selectedCompositeIsMine, setSelectedCompositeIsMine] = useState(true)
  const [compositeName, setCompositeName] = useState('')
  const [compositeDesc, setCompositeDesc] = useState('')
  // KBNL-124: 설명 입력 기본 1줄 + 토글 펼침 (워크벤치 descExpanded 와 동일 패턴)
  const [descExpanded, setDescExpanded] = useState(false)
  const [savingComposite, setSavingComposite] = useState(false)

  // ── Composite View 결합 선택 (기존 composite를 새로운 composite에 포함) ──
  const [selectedCompositeRefs, setSelectedCompositeRefs] = useState(new Set())

  // ── 이름 바꾸기 ────────────────────────────────────────────────────
  const [renamingComposite, setRenamingComposite] = useState(null)
  const [renameValue, setRenameValue] = useState('')

  // ── 복제 (Duplicate) ───────────────────────────────────────────────
  const [duplicateDialog, setDuplicateDialog] = useState(null) // { sourceName, defaultName } | null
  const [duplicating, setDuplicating] = useState(false)

  // ── 출처(provenance) 추적 ───────────────────────────────────────────
  const [currentSources, setCurrentSources] = useState([])

  // KBNL-68: 자식 SV 별 토큰 수 — 본문 헤더 chip 표시용
  const [childTokenCounts, setChildTokenCounts] = useState([])
  // KBNL-69: 본문 헤더에 표시할 전체 CV 토큰 수 (마지막 저장 기준)
  const [compositeTokenCount, setCompositeTokenCount] = useState(null)

  // KBNL-74 후속: 조립 결과의 각 sub-SV 카드별 "유사 SV 찾기".
  // 카드의 [🔍 유사 SV 찾기] 버튼 클릭 시 그 sub-SV(name + description + 소스 테이블)
  // 를 query 로 모달 오픈. null 이면 모달 닫힘.
  // (이전엔 CV 전체 name+desc 으로 모달 띄웠지만 검색이 거의 0건 — 의미 없음.)
  const [recommendForView, setRecommendForView] = useState(null)
  // shape: { name: string, query: string } | null

  // ── 원본 SQL 보기 모달 ────────────────────────────────────────────
  const [sqlModal, setSqlModal] = useState(null) // { name, sql_text }

  // ── 생성 결과 ───────────────────────────────────────────────────────
  const [yaml_, setYaml] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)

  // ── 조합 모드 (단순 / AI) + AI 프롬프트 패널 ─────────────────────────
  const [combineMode, setCombineMode] = useState(null)  // 'simple' | 'ai' (loading 중인 모드)
  const [showAiPanel, setShowAiPanel] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  // KBNL-139: AI 조합 백그라운드 잡 — 컴포지트 이름(프로젝트)별 잡 미러를 폴링.
  // 여러 프로젝트에서 병렬 실행 가능(각자 name 키). 현재 보는 컴포지트의 잡만 배너 표시.
  const [aiJobs, setAiJobs] = useState([])         // 현재 컴포지트 이름의 ai_combine 잡들
  const [pollVersion, setPollVersion] = useState(0)  // 리뷰 #1: 폴링 대상 변경 시 effect 재가동 트리거
  const appliedJobsRef = useRef(new Set())         // 이미 적용/처리한 job_id (중복 적용 방지)
  const liveNameRef = useRef('')                   // 현재 보고 있는 컴포지트 이름(최신) — direct 적용 가드
  // 리뷰 #1: 현재 뷰에서 fire 한 AI 조합 jobName(이름 없는 컴포지트의 생성 jobName 포함).
  // 폴링은 (현재 컴포지트 이름 || 이 값) 단일 대상만 본다 — 다른 컴포지트의 잡이 배너에
  // 새어 잘못 적용되는 것을 방지. 컴포지트 전환 시 초기화하고, 변경 시 pollVersion bump.
  const firedNameRef = useRef('')
  // KBNL-141: 내가 돌리는 AI 조합(running) — 좌측 목록 항목에 스피너 매칭용.
  const runningJobs = useRunningJobs(true)

  // ── GCS Export 다이얼로그 (KBNL-27) ──────────────────────────────────
  const [showExport, setShowExport] = useState(false)
  const [showImport, setShowImport] = useState(false)  // KBNL-140: GCS Import 다이얼로그

  // ── KBNL-141: 조합 재료 내용 미리보기 (읽기 전용) ─────────────────────
  // 조합하기 전에 후보 SV/CV 의 YAML 내용을 빠르게 확인. { kind, name, owner, ownerName, description, loading, content }
  const [previewItem, setPreviewItem] = useState(null)
  const openPreview = useCallback(async (item, kind) => {
    const owner = item.owner_user_id || item.user_id || ''
    setPreviewItem({
      kind, name: item.name, owner,
      ownerName: item.owner_name || owner,
      description: item.description || '', loading: true, content: '',
    })
    try {
      let content = ''
      if (kind === 'composite') {
        const d = await api.getCompositeView(item.name, storageSettings, owner)
        content = d.composite_yaml || d.yaml_string || ''
      } else {
        const d = await api.getView(item.name, storageSettings, owner)
        content = d.semantic_yaml || d.metric_view_yaml || d.yaml_string || ''
      }
      // 그 사이 다른 항목을 열었으면(name+owner 변경) 덮어쓰지 않음.
      setPreviewItem(prev => (prev && prev.name === item.name && prev.owner === owner)
        ? { ...prev, loading: false, content } : prev)
    } catch (e) {
      setPreviewItem(prev => (prev && prev.name === item.name && prev.owner === owner)
        ? { ...prev, loading: false, content: `# 불러오기 실패: ${e.message}` } : prev)
      showToast('error', `미리보기 로드 실패: ${e.message}`)
    }
  }, [storageSettings, showToast])

  // ── 카드 리스트 — 클릭한 view 카드로 스크롤 (의존관계 chip → 카드)
  // KBNL-141(eslint react-hooks/refs): ref 를 일절 쓰지 않고 data-view-name 으로 DOM 을
  // 직접 조회 — scrollToView 가 ref 를 읽으면 map 콜백 내 onClick 참조까지 규칙이 막는다.
  const scrollToView = useCallback((name) => {
    const sel = `.composite-summary-list [data-view-name="${(window.CSS?.escape?.(name)) || name}"]`
    const el = document.querySelector(sel)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // view_name → source provenance lookup (origin badge, 원본 SQL 버튼 표시 여부)
  const sourceMap = {}
  for (const s of currentSources || []) sourceMap[s.view_name] = s

  // KBNL-140: 뷰 이름 → 토큰 수 (조합 결과 카드에 표시 — 자식 SV 토큰 줄을 카드로 통합)
  const tokenByName = {}
  for (const c of childTokenCounts || []) if (c?.name) tokenByName[c.name] = c.count

  // ── 파일 로드 ───────────────────────────────────────────────────────
  const fileRef = useRef(null)

  // ── 스플리터 ────────────────────────────────────────────────────────
  // 사이드바가 2열로 늘어나면서 우측 영역이 좁아짐 → 정보창보다 편집기에 더 많이 할당.
  // 사용자가 cv-resizer 로 조정 가능.
  const [editorPct, setEditorPct] = useState(60)
  // 조합 재료 column 안에서 [Composite 결합] vs [Semantic Views] 의 세로 분할 비율(%).
  // 기본 50/50, .composite-row-splitter 드래그로 조정.
  const [materialsPct, setMaterialsPct] = useState(50)
  const materialsRef = useRef(null)
  const splitterRef = useRef(null)
  const containerRef = useRef(null)

  // ── Semantic View 목록 로드 ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoadingViews(true)
      try {
        const list = await api.listViews(storageSettings)
        if (!cancelled) setSavedViews(list)
      } catch (e) {
        console.error('Failed to load views:', e)
        showToast('error', '뷰 목록 로드 실패: ' + e.message)
      } finally {
        if (!cancelled) setLoadingViews(false)
      }
    })()
    return () => { cancelled = true }
  }, [storageSettings])

  // ── Composite View 목록 로드 ────────────────────────────────────────
  const loadComposites = useCallback(async () => {
    try {
      const list = await api.listCompositeViews(storageSettings)
      setSavedComposites(list)
    } catch (e) {
      console.error('Failed to load composite views:', e)
      showToast('error', 'Composite View 목록 로드 실패: ' + e.message)
    }
  }, [storageSettings])

  // KBNL-141(eslint): 동기 setState in effect 경고 회피 — setTimeout 으로 디커플.
  // 비동기 로드(loadComposites)를 타이머 콜백에서 호출해 effect 본문의 직접 setState 제거.
  useEffect(() => {
    const t = setTimeout(() => { loadComposites() }, 0)
    return () => clearTimeout(t)
  }, [loadComposites])

  // ── Composite View 선택(로드) ───────────────────────────────────────
  const handleSelectComposite = useCallback(async (name, ownerUserId = '') => {
    try {
      const data = await api.getCompositeView(name, storageSettings, ownerUserId)
      setSelectedComposite(name)
      setSelectedCompositeOwner(data.owner_user_id || data.user_id || ownerUserId || '')
      setSelectedCompositeIsMine(data.is_mine !== false)
      setCompositeName(data.name || name)
      // 리뷰 #1: 컴포지트 전환 — 이전 뷰에서 fire 한 폴링 대상 초기화(잡 누수 방지).
      firedNameRef.current = ''
      setCompositeDesc(data.description || '')
      // KBNL-75: CV 본체 = composite_yaml 우선, legacy 'yaml_string' fallback
      const cvYaml = data.composite_yaml || data.yaml_string || ''
      setYaml(cvYaml)
      setResult(null)
      setCurrentSources(data.sources || [])
      // KBNL-68: 저장된 자식 SV 별 토큰 수 (없으면 빈 배열)
      setChildTokenCounts(Array.isArray(data.child_token_counts) ? data.child_token_counts : [])
      // KBNL-69: 본문 헤더용 전체 CV 토큰 수
      setCompositeTokenCount(data.token_count ?? null)
      // KBNL-141(리뷰): 저장된 선택 상태 복원 — SV(selected_views)와 CV(included_composites)를
      // 각각 FIFO owner 매칭(restoreSelectionKeys)으로 복원. 둘 다 항상 set(없으면 빈 Set)해서
      // 이전 컴포지트의 체크 상태가 남지 않게 한다. (같은 이름 다른 owner 도 둘 다 보존)
      setSelectedMetrics(new Set(restoreSelectionKeys(data.selected_views, data.selected_views_meta)))
      setSelectedCompositeRefs(new Set(restoreSelectionKeys(data.included_composites, data.included_composites_meta)))
      showToast('success', `'${name}' 로드 완료`)
      if (cvYaml.trim()) {
        try {
          const res = await api.compositeAssemble({ composite_yaml: cvYaml }, storageSettings)
          setResult(res)
          showAssembleWarnings(res, showToast)
        } catch (asmErr) {
          // KBNL-136: 조립 실패를 조용히 삼키지 않음 — 기존엔 catch 가 무시해서
          // 미리보기 패널이 빈 화면으로만 보였고 원인 파악이 불가능했음.
          // YAML 은 이미 에디터에 로드돼 있으니, 조합 결과만 비우고 사유를 알린다.
          setResult(null)
          showToast('error', `조합 결과 갱신 실패: ${asmErr.message} — YAML 은 에디터에서 확인/수정할 수 있습니다.`)
        }
      }
    } catch (e) {
      showToast('error', `로드 실패: ${e.message}`)
    }
  }, [storageSettings, showToast])

  // ── Composite View 저장 ─────────────────────────────────────────────
  const handleSaveComposite = useCallback(async () => {
    const name = compositeName.trim()
    if (!name) { showToast('error', '이름을 입력하세요.'); return }
    if (!yaml_.trim()) { showToast('error', '저장할 YAML이 없습니다.'); return }
    setSavingComposite(true)
    try {
      // KBNL-46-C / 리뷰 #1: selected_views_meta 로 owner 정보도 같이 전송.
      // 복합 키 (owner::name) 에서 splitItemKey 로 분리 — 같은 이름 다른 owner 여도 정확.
      const buildMeta = (keys) => [...keys]
        .map(splitItemKey)
        .filter(m => m.name && m.owner_user_id)
      const namesOnly = (keys) => [...keys].map(k => splitItemKey(k).name)
      // KBNL-141(리뷰): SV 메타와 CV 메타를 분리 저장 — 재오픈 시 각각 정확히 복원.
      const saveRes = await api.saveCompositeView({
        name,
        description: compositeDesc,
        // KBNL-75: yaml_string → composite_yaml
        composite_yaml: yaml_,
        selected_views: namesOnly(selectedMetrics),
        selected_views_meta: buildMeta(selectedMetrics),
        included_composites: namesOnly(selectedCompositeRefs),
        included_composites_meta: buildMeta(selectedCompositeRefs),
        sources: currentSources,
      }, storageSettings)
      setSelectedComposite(name)
      setSelectedCompositeOwner(saveRes?.owner_user_id || storageSettings?.user_id || '')
      setSelectedCompositeIsMine(true)
      // KBNL-69: 저장 응답의 token_count + child_token_counts 로 헤더 갱신
      setCompositeTokenCount(saveRes?.token_count ?? null)
      if (Array.isArray(saveRes?.child_token_counts)) {
        setChildTokenCounts(saveRes.child_token_counts)
      }
      await loadComposites()
      showToast('success', `'${name}' 저장 완료`)
    } catch (e) {
      showToast('error', `저장 실패: ${e.message}`)
    } finally {
      setSavingComposite(false)
    }
  }, [compositeName, compositeDesc, yaml_, selectedMetrics, selectedCompositeRefs, currentSources, storageSettings, showToast, loadComposites])

  // ── Composite View 삭제 ─────────────────────────────────────────────
  const handleDeleteComposite = useCallback(async () => {
    if (!selectedComposite) return
    if (!selectedCompositeIsMine) {
      showToast('error', '다른 사용자의 Composite View 는 삭제할 수 없습니다.')
      return
    }
    if (!window.confirm(`'${selectedComposite}'을(를) 삭제할까요?`)) return
    try {
      await api.deleteCompositeView(selectedComposite, storageSettings)
      setSelectedComposite(null)
      setSelectedCompositeOwner('')
      setSelectedCompositeIsMine(true)
      setCompositeName('')
      setCompositeDesc('')
      setYaml('')
      setResult(null)
      await loadComposites()
      showToast('success', `'${selectedComposite}' 삭제 완료`)
    } catch (e) {
      showToast('error', `삭제 실패: ${e.message}`)
    }
  }, [selectedComposite, selectedCompositeIsMine, storageSettings, showToast, loadComposites])

  // ── 새로 만들기 ─────────────────────────────────────────────────────
  const handleNewComposite = useCallback(() => {
    setSelectedComposite(null)
    setSelectedCompositeOwner('')
    setSelectedCompositeIsMine(true)
    setCompositeName('')
    // 리뷰 #1: 새 컴포지트 — 이전 뷰의 폴링 대상 초기화(잡 누수 방지).
    firedNameRef.current = ''
    setCompositeDesc('')
    setYaml('')
    setResult(null)
    setSelectedMetrics(new Set())
    setSelectedCompositeRefs(new Set())
    setCurrentSources([])
    setChildTokenCounts([])
    setCompositeTokenCount(null)
  }, [])

  // ── Composite View 이름 바꾸기 ──────────────────────────────────────
  const handleStartRename = useCallback((item) => {
    setRenamingComposite(itemKey(item))
    setRenameValue(item.name)
  }, [])

  const handleConfirmRename = useCallback(async () => {
    const { name: oldName, owner_user_id: oldOwner } = splitItemKey(renamingComposite || '')
    const newName = renameValue.trim()
    if (!oldName || !newName || newName === oldName) {
      setRenamingComposite(null)
      return
    }
    try {
      await api.renameCompositeView(oldName, newName, storageSettings)
      if (selectedComposite === oldName && selectedCompositeOwner === oldOwner) {
        setSelectedComposite(newName)
        setCompositeName(newName)
      }
      await loadComposites()
      showToast('success', `'${oldName}' → '${newName}' 이름 변경 완료`)
    } catch (e) {
      showToast('error', `이름 변경 실패: ${e.message}`)
    } finally {
      setRenamingComposite(null)
    }
  }, [renamingComposite, renameValue, selectedComposite, selectedCompositeOwner, storageSettings, showToast, loadComposites])

  const handleCancelRename = useCallback(() => {
    setRenamingComposite(null)
  }, [])

  // ── Composite View 복제 (Duplicate) ──────────────────────────────────
  // 본인 항목 hover 시 노출되는 ⎘ 버튼 → 다이얼로그 → 원본 본문(yaml + selected_views
  // + sources 등) 그대로 새 이름으로 저장.
  const openDuplicate = useCallback((c) => {
    const myComposites = (savedComposites || []).filter(x => x.is_mine !== false)
    setDuplicateDialog({
      sourceName: c.name,
      defaultName: suggestDuplicateName(c.name, myComposites),
    })
  }, [savedComposites])

  const handleDuplicate = useCallback(async (newName) => {
    if (!duplicateDialog) return
    const sourceName = duplicateDialog.sourceName
    const finalName = (newName || '').trim()
    if (!finalName) { showToast('error', '이름을 입력하세요.'); return }
    const myComposites = (savedComposites || []).filter(x => x.is_mine !== false)
    if (myComposites.some(c => c.name === finalName)) {
      showToast('error', `이미 '${finalName}' 이름의 Composite View 가 존재합니다.`)
      return
    }
    setDuplicating(true)
    try {
      const src = await api.getCompositeView(sourceName, storageSettings)
      await api.saveCompositeView({ ...src, name: finalName }, storageSettings)
      showToast('success', `'${sourceName}' → '${finalName}' 복제 완료`)
      setDuplicateDialog(null)
      await loadComposites()
    } catch (e) {
      showToast('error', `복제 실패: ${e.message}`)
    } finally {
      setDuplicating(false)
    }
  }, [duplicateDialog, savedComposites, storageSettings, showToast, loadComposites])

  // ── Semantic View 체크박스 토글 (리뷰 #1: key = owner::name 복합 키) ──
  const toggleMetric = useCallback((key) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }, [])

  // ── Composite View 결합 체크박스 토글 (리뷰 #1: 복합 키) ──────────
  const toggleCompositeRef = useCallback((key) => {
    setSelectedCompositeRefs(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }, [])

  // ── KBNL-139: AI 조합 백그라운드 잡 폴링/적용 ─────────────────────────
  // 현재 컴포지트 이름(jobName)으로 workbench 잡 컬렉션을 폴링(이름=프로젝트 키).
  const aiJobName = compositeName.trim()
  // 최신 컴포지트 이름을 ref 로 보관 — fire 후 다른 컴포지트로 이동했는지 판별(direct 적용 가드).
  // 렌더 중 ref 쓰기는 금지라 effect 에서 동기화 (.then 은 async 라 그때 최신값 참조).
  useEffect(() => { liveNameRef.current = aiJobName }, [aiJobName])

  const applyAiJob = useCallback(async (job) => {
    if (!job?.job_id) return
    appliedJobsRef.current.add(job.job_id)
    setAiJobs(prev => prev.filter(j => j.job_id !== job.job_id))
    try {
      const finalYaml = job.result_yaml || ''
      if (finalYaml.trim()) {
        setYaml(finalYaml)
        const asm = await api.compositeAssemble({ composite_yaml: finalYaml }, storageSettings)
        setResult(asm)
        showToast('success', `AI 조합 완료 — ${asm.views?.length || 0}개 뷰 (백그라운드)`)
        showAssembleWarnings(asm, showToast)
      }
    } catch (e) {
      showToast('error', `AI 조합 결과 적용 실패: ${e.message}`)
    } finally {
      api.wbJobsCollect(job.job_id).catch(() => {})
    }
  }, [storageSettings, showToast])

  // KBNL-141: 진행 중 AI 조합 강제 중단 — 서버 태스크 취소 + 잡 정리.
  // 리뷰 #2: cancel 성공 후에만 목록 제거/applied 등록을 확정 — 실패 시 롤백 없이
  //   목록에서 사라져 폴링이 못 되살리는 문제 방지(실패하면 잡은 그대로 유지·계속 폴링).
  const cancelAiJob = useCallback(async (job) => {
    if (!job?.job_id) return
    if (!window.confirm('진행 중인 AI 조합을 중단할까요? 결과는 폐기됩니다.')) return
    try {
      await api.wbJobsCancel(job.job_id)
      appliedJobsRef.current.add(job.job_id)        // 성공 시에만 — 폴링/직접응답 재출현 차단
      if (job.name && firedNameRef.current === job.name) firedNameRef.current = ''
      setAiJobs(prev => prev.filter(j => j.job_id !== job.job_id))
      showToast('info', 'AI 조합을 중단했습니다.')
    } catch (e) {
      showToast('error', `중단 실패: ${e.message} (작업이 계속 진행 중일 수 있습니다)`)
    }
  }, [showToast])

  const pollAiJobs = useCallback(async (name) => {
    const nm = (name || '').trim()
    // 이름 없는 컴포지트 + fire 한 잡도 없음: 방금 fire 한 낙관적 잡만 유지(즉시 피드백).
    if (!nm) {
      setAiJobs(prev => {
        const next = prev.filter(j => j._optimistic && !appliedJobsRef.current.has(j.job_id))
        return next.length === prev.length ? prev : next
      })
      return
    }
    try {
      const r = await api.wbJobsList(nm, storageSettings?.user_id || '')
      const server = (r.jobs || []).filter(j => (j.step?.kind === 'ai_combine')
        && !appliedJobsRef.current.has(j.job_id))
      // KBNL-140 fix: 방금 fire 한 낙관적(optimistic) running 잡은 서버 doc 이 아직
      // 안 생겼을 수 있으므로 유지 — 안 그러면 첫 클릭 직후 폴링이 빈 결과로 스피너를
      // 지워버려 '무반응'처럼 보이고 버튼이 다시 눌려 중복 잡(스피너 2개)이 생긴다.
      setAiJobs(prev => {
        const serverIds = new Set(server.map(j => j.job_id))
        const pendingLocal = prev.filter(j => j._optimistic
          && !serverIds.has(j.job_id) && !appliedJobsRef.current.has(j.job_id))
        return [...server, ...pendingLocal]
      })
      // 자동 적용하지 않음 — 편집/저장 충돌 방지. done 은 배너 [결과 적용] 으로 반영.
    } catch { /* 폴링 실패 무시 */ }
  }, [storageSettings])

  // 현재 컴포지트가 바뀌거나 마운트 시 그 이름의 잡을 폴링(복구) + 8s 주기 폴링.
  // KBNL-140 fix: 폴링 effect 가 pollAiJobs identity 에 의존하면, pollAiJobs 가 매
  // 렌더 재생성될 때(예: storageSettings 가 매 렌더 새 객체) effect → setTimeout →
  // setAiJobs → 재렌더 → effect 재실행의 무한 루프로 UI 가 멈춘다. 그래서 effect 는
  // aiJobName(원시값) + pollVersion 에만 의존하고, 최신 poll 함수는 ref 로 호출한다.
  // 리뷰 #1: 폴링 대상 = 현재 컴포지트 이름 || 현재 뷰에서 fire 한 jobName(이름 없는 경우).
  const pollRef = useRef(pollAiJobs)
  useEffect(() => { pollRef.current = pollAiJobs }, [pollAiJobs])
  useEffect(() => {
    const target = aiJobName || firedNameRef.current
    const run = () => { pollRef.current(target) }
    const t0 = setTimeout(run, 0)   // 초기 1회 (동기 스택 분리)
    const t = target ? setInterval(run, 8000) : null
    return () => { clearTimeout(t0); if (t) clearInterval(t) }
  }, [aiJobName, pollVersion])

  // ── 조합 (단순 = 기존 로직, AI = LLM 호출) ──────────────────────────
  // mode: 'simple' = 기존 deterministic merge (composite.converter)
  //       'ai'     = LLM 으로 user_prompt 반영 통합
  const handleCombine = useCallback(async (mode = 'simple') => {
    const totalSelected = selectedMetrics.size + selectedCompositeRefs.size
    // 리뷰 #1: 복합 키에서 splitItemKey 로 owner/name 분리. 이름 충돌 시
    // owner mapping 이 덮어쓰여 잘못된 owner 가 전송되는 회귀를 방지.
    const buildOwnerMeta = (keys) => [...keys]
      .map(splitItemKey)
      .filter(m => m.name && m.owner_user_id)
    const namesOnly = (keys) => [...keys].map(k => splitItemKey(k).name)
    if (totalSelected === 0) { showToast('warning', '뷰를 1개 이상 선택하세요.'); return }

    // ── AI 모드 분기: 프롬프트 + 백엔드 호출 후 단순 모드와 동일 후처리 ──
    if (mode === 'ai') {
      // KBNL-140: 프롬프트 미입력 허용 — 비면 백엔드가 기본 지시문으로 조합.
      if (!settings?.project_id) { showToast('error', '설정에서 Project ID 를 입력하세요.'); return }
      // KBNL-139: 백그라운드 잡으로 fire — UI 블로킹 X. 컴포지트 이름(프로젝트)별 잡이라
      // 다른 컴포지트로 가서 또 실행 가능(병렬). 결과는 폴링 배너가 적용/회수.
      const jobName = compositeName.trim()
        || `AI조합:${namesOnly(selectedMetrics).slice(0, 2).join(',') || 'new'}`
      const jobId = (window.crypto?.randomUUID?.() || `ai-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)
      // sources 기록 (AI 결과 origin)
      const aiSources = []
      for (const c of selectedCompositeRefs) aiSources.push({ view_name: splitItemKey(c).name, origin: 'composite', sql_query_name: null })
      for (const m of selectedMetrics) aiSources.push({ view_name: splitItemKey(m).name, origin: 'ai_combine', sql_query_name: null })
      setCurrentSources(aiSources)
      setShowAiPanel(false)
      // 리뷰 #1: 현재 뷰에서 fire 한 jobName 을 폴링 대상으로 지정 + effect 재가동.
      // (이름 없는 컴포지트도 이 단일 대상으로 완료를 회수. 전환 시 초기화되어 누수 없음)
      firedNameRef.current = jobName
      setPollVersion(v => v + 1)
      // KBNL-140 fix: 즉시 낙관적 running 잡 추가 → 스피너/배너 즉시 표시 + 버튼 즉시
      // 비활성화(중복 클릭 방지). 서버 잡(같은 job_id)이 폴링으로 오면 자연히 대체됨.
      setAiJobs(prev => prev.some(j => j.job_id === jobId)
        ? prev
        : [...prev, { job_id: jobId, name: jobName, status: 'running', step: { kind: 'ai_combine' }, _optimistic: true }])
      api.compositeAiCombine({
        selected_composites: namesOnly(selectedCompositeRefs),
        selected_views: namesOnly(selectedMetrics),
        selected_composites_meta: buildOwnerMeta(selectedCompositeRefs),
        selected_views_meta: buildOwnerMeta(selectedMetrics),
        user_prompt: aiPrompt,
        project_id: settings.project_id,
        location: settings.location || null,
        model_name: settings.model_name || null,
        storage_type:   storageSettings?.storage_type || APP_DEFAULTS.storage_type,
        gcs_path:       storageSettings?.gcs_path || '',
        fs_project:     storageSettings?.fs_project || '',
        fs_database:    storageSettings?.fs_database || '',
        fs_collection:  storageSettings?.fs_collection || '',
        user_id:        storageSettings?.user_id || '',
        job: { job_id: jobId, name: jobName, step: { kind: 'ai_combine' } },
      }).then(res => {
        // 코드 리뷰 #1: 잡 미러가 실패해도 백엔드는 결과를 직접 반환하므로, 응답 본문을
        // 우선 적용해 유실 방지. 단 fire 후 다른 컴포지트로 이동했으면(병렬) 엉뚱한
        // 편집기 덮어쓰기 방지 — 그 경우 폴링에 위임(미러 성공 시 돌아오면 적용).
        // 이름 없는 컴포지트는 aiJobName(''), liveNameRef 가 jobName 과 다르므로 firedNameRef
        // 로도 '현재 뷰 유지' 여부를 판정한다(전환 시 firedNameRef 는 초기화됨).
        const directYaml = res?.composite_yaml || res?.yaml_string || ''
        const stillOnView = liveNameRef.current === jobName || firedNameRef.current === jobName
        if (directYaml.trim() && !appliedJobsRef.current.has(jobId) && stillOnView) {
          appliedJobsRef.current.add(jobId)
          firedNameRef.current = ''
          setAiJobs(prev => prev.filter(j => j.job_id !== jobId))
          setYaml(directYaml)
          setResult(res)
          const fixN = res.ai_combine?.fix_attempts || 0
          showToast('success', `AI 조합 완료 — ${res.views?.length || 0}개 뷰` + (fixN ? ` (검증 재시도 ${fixN}회)` : ''))
          showAssembleWarnings(res, showToast)
          api.wbJobsCollect(jobId).catch(() => {})
        } else {
          pollAiJobs(jobName)
        }
      }).catch((e) => {
        // 요청 자체 실패 — 낙관적 running 잡 제거(영구 스피너 방지) + 사유 토스트.
        // 서버에 실제 잡이 생겼다면 이후 인터벌 폴링이 회수해 표시한다.
        setAiJobs(prev => prev.filter(j => !(j._optimistic && j.job_id === jobId)))
        showToast('error', `AI 조합 요청 실패: ${e.message}`)
        pollAiJobs(jobName)
      })
      showToast('info',
        `백그라운드에서 AI 조합 시작 (${jobName}) — 완료되면 알려드립니다. 다른 작업을 계속하셔도 됩니다.`)
      return
    }

    // ── 단순 조합 (기존 로직) ──
    setLoading(true); setCombineMode('simple')
    try {
      let sources = []

      // 1) 선택된 Composite View들의 YAML + sources를 로드
      let mergedYamlParts = []
      // CV 본문 안에 이미 정의된 sub-SV 이름 모음 — selectedMetrics 와 dedup 용.
      // 같은 이름의 SV 정의가 두 번 들어가면 assembler 가 'semantic_view 이름 중복' fatal.
      const cvDefinedNames = new Set()
      // 리뷰 #1: 복합 키 → name 추출. owner_user_id 는 cross-owner getCompositeView 에 전달.
      for (const cKey of selectedCompositeRefs) {
        const { name: cName, owner_user_id: cOwner } = splitItemKey(cKey)
        try {
          const cData = await api.getCompositeView(cName, storageSettings, cOwner)
          // KBNL-75: CV 본체 = composite_yaml 우선, legacy fallback
          const cYaml = cData.composite_yaml || cData.yaml_string || ''
          if (cYaml.trim()) {
            mergedYamlParts.push(cYaml.trim())
            for (const n of extractViewNamesFromYaml(cYaml)) cvDefinedNames.add(n)
          }
          // 출처 전파: 기존 composite의 sources를 상속
          if (cData.sources?.length) {
            sources.push(...cData.sources)
          } else {
            // sources 없는 기존 CV → composite origin으로 기록
            const cViewNames = extractViewNamesFromYaml(cYaml)
            sources.push(...cViewNames.map(vn => ({
              view_name: vn, origin: 'composite', sql_query_name: null,
            })))
          }
        } catch (e) {
          console.warn(`Composite View '${cName}' 로드 실패:`, e)
        }
      }

      // 1.5) selectedMetrics 중 CV 안에 이미 정의된 것은 자동 제외 (이름 중복 사전 차단).
      // assembler 가 fatal raise 하던 'semantic_view 이름 중복' 회귀를 사용자 동작 시점에 dedup.
      // 리뷰 #1: selectedMetrics element = 복합 키. name 으로 dedup 비교.
      const selectedMetricNames = [...selectedMetrics].map(k => splitItemKey(k).name)
      const removedDup = selectedMetricNames.filter(n => cvDefinedNames.has(n))
      const dedupedMetricsKeys = new Set(
        [...selectedMetrics].filter(k => !cvDefinedNames.has(splitItemKey(k).name)),
      )
      if (removedDup.length > 0) {
        showToast('warning',
          `선택한 CV 안에 이미 정의된 SV 자동 제외: ${removedDup.join(', ')} ` +
          `(중복 정의 → 'semantic_view 이름 중복' 차단)`,
        )
      }

      // 2) 선택된 Metric Views (dedup 후) 로 새 Composite 생성
      let generatedYaml = ''
      if (dedupedMetricsKeys.size > 0) {
        const res = await api.compositeGenerate({
          view_names: namesOnly(dedupedMetricsKeys),
          selected_views_meta: buildOwnerMeta(dedupedMetricsKeys),
          storage_type:   storageSettings?.storage_type || APP_DEFAULTS.storage_type,
          gcs_path:       storageSettings?.gcs_path || '',
          fs_project:     storageSettings?.fs_project || '',
          fs_database:    storageSettings?.fs_database || '',
          fs_collection:  storageSettings?.fs_collection || '',
          user_id:        storageSettings?.user_id || '',
        })
        // KBNL-75: 신규 composite_yaml 우선, legacy fallback
        generatedYaml = res.composite_yaml || res.yaml_string || ''
        // KBNL-137: 변환 단계 경고(빈 본문/0필드/형식 깨짐) 노출
        showAssembleWarnings(res, showToast)
        // Metric View → origin: generator
        // 리뷰 #1: 복합 키에서 name 만 view_name 으로 사용.
        for (const mKey of dedupedMetricsKeys) {
          sources.push({ view_name: splitItemKey(mKey).name, origin: 'generator', sql_query_name: null })
        }
      }

      // 중복 제거 (같은 view_name이 여러 번 나올 수 있음)
      const seen = new Set()
      sources = sources.filter(s => {
        if (seen.has(s.view_name)) return false
        seen.add(s.view_name)
        return true
      })
      setCurrentSources(sources)

      // 3) YAML 병합 — 기존 Composite + 새로 생성된 것
      // 리뷰 #3: 두 CV 가 같은 sub-SV 를 포함하면 단순 concatenate 시 assembler
      // 'semantic_view 이름 중복' fatal. mergeCompositeYamls 가 name 기준 dedup
      // 하고 onDuplicate 콜백으로 제거된 이름들을 모아 사용자에게 toast 로 알림.
      const mergeDups = []
      const onDup = (n) => mergeDups.push(n)
      let finalYaml = ''
      if (mergedYamlParts.length > 0 && generatedYaml) {
        finalYaml = mergeCompositeYamls([...mergedYamlParts, generatedYaml], { onDuplicate: onDup })
      } else if (mergedYamlParts.length > 0) {
        finalYaml = mergedYamlParts.length === 1
          ? mergedYamlParts[0]
          : mergeCompositeYamls(mergedYamlParts, { onDuplicate: onDup })
      } else {
        finalYaml = generatedYaml
      }
      if (mergeDups.length > 0) {
        const uniq = [...new Set(mergeDups)]
        showToast('warning',
          `여러 CV 에 중복 정의된 SV 자동 제거 (첫 정의 유지): ${uniq.join(', ')} ` +
          `— 의도한 owner 의 정의가 아니면 결합 순서를 조정하세요.`,
        )
      }

      setYaml(finalYaml)

      // 4) 즉시 조립
      if (finalYaml.trim()) {
        const assembleRes = await api.compositeAssemble({ composite_yaml: finalYaml }, storageSettings)
        setResult(assembleRes)
        showToast('success', `${assembleRes.views?.length || 0}개 뷰 → Composite View 생성 완료`)
        showAssembleWarnings(assembleRes, showToast)
      }
    } catch (e) { showToast('error', e.message) }
    finally { setLoading(false); setCombineMode(null) }
  // savedViews/savedComposites 는 더 이상 직접 참조 안 함 (owner 정보가 selectedMetrics
  // 복합 키에 이미 포함). 단, selectedMetrics 자체가 deps 에 있으므로 list 변경 시 함수
  // 재계산 보장은 그대로.
  }, [selectedMetrics, selectedCompositeRefs, storageSettings, showToast, aiPrompt, settings, compositeName, pollAiJobs])

  // ── 재조립 ──────────────────────────────────────────────────────────
  const handleAssemble = useCallback(async () => {
    if (!yaml_.trim()) { showToast('warning', 'YAML을 입력하세요.'); return }
    setLoading(true)
    try {
      const res = await api.compositeAssemble({ composite_yaml: yaml_ }, storageSettings)
      setResult(res)
      showToast('success', `${res.views?.length || 0}개 뷰 재조립 완료`)
      showAssembleWarnings(res, showToast)
    } catch (e) { showToast('error', e.message) }
    finally { setLoading(false) }
  }, [yaml_, storageSettings, showToast])

  // ── 파일/샘플 ───────────────────────────────────────────────────────
  const handleLoadFile = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => { setYaml(ev.target.result); setResult(null); showToast('success', `${file.name} 로드 완료`) }
    reader.readAsText(file, 'utf-8'); e.target.value = ''
  }, [showToast])

  // ── 원본 SQL 보기 ──────────────────────────────────────────────────
  const handleViewSql = useCallback((viewName) => {
    const src = currentSources.find(s => s.view_name === viewName)
    if (src?.sql_text) {
      setSqlModal({ name: src.sql_query_name || viewName, sql_text: src.sql_text })
    } else {
      showToast('warning', '이 뷰에 저장된 원본 SQL이 없습니다.')
    }
  }, [currentSources, showToast])

  const handleLoadSample = useCallback(async () => {
    try {
      const text = await api.compositeSample()
      setYaml(text); setResult(null); showToast('success', '샘플 YAML 로드 완료')
    } catch { showToast('error', '샘플 로드 실패') }
  }, [showToast])

  // ── 스플리터 드래그 ─────────────────────────────────────────────────
  const onSplitterMouseDown = useCallback((e) => {
    e.preventDefault()
    const container = containerRef.current
    if (!container) return
    const sidebarWidth = 220
    const startX = e.clientX
    const startPct = editorPct

    const onMove = (ev) => {
      const containerRect = container.getBoundingClientRect()
      const availableWidth = containerRect.width - sidebarWidth
      if (availableWidth <= 0) return
      const dx = ev.clientX - startX
      const dpct = (dx / availableWidth) * 100
      const newPct = Math.min(75, Math.max(20, startPct + dpct))
      setEditorPct(newPct)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [editorPct])

  // 조합 재료 column 안의 [Composite 결합] / [Semantic Views] 사이 row-splitter.
  // header (cv-group-header + 각 subsection-header) 가 차지하는 공간 때문에
  // 비율은 cv-section-materials 전체 높이가 아닌 두 subsection 합 높이 기준.
  const onMaterialsSplitterMouseDown = useCallback((e) => {
    e.preventDefault()
    const startY = e.clientY
    const startPct = materialsPct
    const sectionEl = materialsRef.current
    // 두 subsection 의 실제 합산 높이를 측정 (splitter + header 제외)
    const subsections = sectionEl ? sectionEl.querySelectorAll('.cv-subsection') : []
    if (subsections.length < 2) return
    const totalH = subsections[0].getBoundingClientRect().height
                 + subsections[1].getBoundingClientRect().height
    if (totalH <= 0) return

    const onMove = (ev) => {
      const dy = ev.clientY - startY
      const dpct = (dy / totalH) * 100
      const newPct = Math.min(85, Math.max(15, startPct + dpct))
      setMaterialsPct(newPct)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'row-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [materialsPct])

  const cmExtensions = [yaml(), search({ top: true }), Prec.highest(keymap.of([
    ...searchKeymap,
    { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
  ]))]

  // 현재 편집 중인 Composite와 같은 owner/name 항목만 결합 목록에서 제외
  const selectedCompositeKey = selectedComposite ? `${selectedCompositeOwner}::${selectedComposite}` : ''
  const availableComposites = savedComposites.filter(c => itemKey(c) !== selectedCompositeKey)

  // Saved Composites 검색 — 좌측 핵심 navigation. name + description 으로 매치
  const [compQuery, setCompQuery] = useState('')
  // KBNL-141: 목록 소유자 필터 토글 — 기본 '내것만 보기'(false=others 숨김).
  const [showOthersComposites, setShowOthersComposites] = useState(false)
  const filteredSavedComposites = useMemo(() => {
    const base = showOthersComposites
      ? savedComposites
      : savedComposites.filter(c => c.is_mine !== false)
    return filterByText(base, compQuery, ['name', 'description'])
  }, [savedComposites, compQuery, showOthersComposites])

  // Composite 결합 (selectedCompositeRefs) 서브 리스트 검색
  const [refQuery, setRefQuery] = useState('')
  // KBNL-141: 결합 후보 소유자 필터 토글 — 기본 '내것만'.
  const [showOthersRefs, setShowOthersRefs] = useState(false)
  const filteredAvailableComposites = useMemo(() => {
    const base = showOthersRefs
      ? availableComposites
      : availableComposites.filter(c => c.is_mine !== false)
    return filterByText(base, refQuery, ['name', 'description'])
  }, [availableComposites, refQuery, showOthersRefs])

  // Semantic Views (savedViews) 서브 리스트 검색
  const [metricQuery, setMetricQuery] = useState('')
  // KBNL-141: Semantic View 소유자 필터 토글 — 기본 '내것만'.
  const [showOthersMetrics, setShowOthersMetrics] = useState(false)
  const filteredSavedViews = useMemo(() => {
    const base = showOthersMetrics
      ? savedViews
      : savedViews.filter(v => v.is_mine !== false)
    return filterByText(base, metricQuery, ['name', 'description'])
  }, [savedViews, metricQuery, showOthersMetrics])

  return (
    <div ref={containerRef} style={{ display: 'flex', gap: 0, width: '100%', height: '100%', overflow: 'hidden' }}>
      {/* ── 1열: 사이드바 — 메인(Saved Composites) + 조합 재료 그룹 ─────
           split-cols modifier 로 가로 2열 레이아웃 활성화 (SqlConverter 는 single-col 기본). */}
      <div className="composite-sidebar split-cols">
        {/* ───── 메인 섹션: 저장된 Composite 탐색/편집 진입점 ───── */}
        <section className="cv-section cv-section-main">
        <div className="composite-sidebar-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13 }}>📚 Composite Views</span>
          <button
            className="btn-xs cv-owner-toggle"
            onClick={() => setShowOthersComposites(v => !v)}
            title={showOthersComposites
              ? '클릭하면 내 Composite View 만 표시'
              : '클릭하면 다른 사람의 Composite View 도 함께 표시'}
          >{showOthersComposites ? '👥 전체 보기' : '🙋 내것만'}</button>
        </div>
        {/* Sidebar 의 '새 Semantic View' 와 동일 스타일 (.btn-new) — 시각 일관성 */}
        <button className="btn-new" onClick={handleNewComposite}>
          <span>+</span> 새 Composite View
        </button>
        {/* Saved Composites 검색 bar (Ctrl+K) */}
        <div style={{ display: 'flex', padding: '4px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)' }}>
          <SearchInput
            value={compQuery}
            onChange={setCompQuery}
            placeholder="Composite 검색 (Ctrl+K)"
            total={showOthersComposites
              ? savedComposites.length
              : savedComposites.filter(c => c.is_mine !== false).length}
            matched={filteredSavedComposites.length}
            compact
          />
        </div>
        {/* Saved Composites — Explore 와 동일한 std-list 스타일.
            협업 모드에선 owner_name / is_mine 메타가 응답에 포함되어 작성자/배지를 노출.
            2열 레이아웃에서 자기 column 의 전체 높이를 차지 (maxHeight 제거). */}
        <div className="std-list">
          {savedComposites.length === 0 ? (
            <div className="std-list-empty">저장된 Composite View가 없습니다.</div>
          ) : filteredSavedComposites.length === 0 ? (
            <div className="std-list-empty">조건에 맞는 Composite 없음</div>
          ) : (
            filteredSavedComposites.map(c => {
              const ownerName = c.owner_name || c.owner_user_id || ''
              const isMine = c.is_mine !== false
              const cKey = itemKey(c)
              const isRenaming = renamingComposite === cKey
              const isSelected = selectedCompositeKey === cKey
              const tooltip = ownerName
                ? [
                    `작성자: ${ownerName}`,
                    c.owner_user_id ? `사번: ${c.owner_user_id}` : null,
                    isMine ? '(더블클릭으로 이름 변경)' : null,
                  ].filter(Boolean).join('\n')
                : '더블클릭으로 이름 변경'
              return (
                <div
                  key={cKey}
                  onClick={() => { if (!isRenaming) handleSelectComposite(c.name, itemOwner(c)) }}
                  onDoubleClick={() => { if (isMine) handleStartRename(c) }}
                  className={`std-list-item ${isSelected ? 'selected' : ''}`}
                  title={tooltip}
                >
                  <div className="std-list-name-row">
                    {isRenaming ? (
                      <input
                        autoFocus
                        className="std-list-rename-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleConfirmRename()
                          if (e.key === 'Escape') handleCancelRename()
                        }}
                        onBlur={handleConfirmRename}
                        onClick={e => e.stopPropagation()}
                        onDoubleClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span className="std-list-name">
                        <HighlightText text={c.name} query={compQuery} />
                      </span>
                    )}
                    {/* KBNL-141: AI 조합 진행 중이면 목록에 스피너 */}
                    {!isRenaming && runningJobs.aiCombine.has(cKey) && (
                      <span className="spinner" title="AI 조합 진행 중…"
                        style={{ width: 12, height: 12, flexShrink: 0 }} />
                    )}
                    {ownerName && !isRenaming && (
                      <span className="std-list-owner" title={tooltip}>
                        <HighlightText text={ownerName} query={compQuery} />
                        {isMine && <span className="std-list-user-badge">나</span>}
                      </span>
                    )}
                    {/* 본인 항목에만 노출되는 hover 액션 — 복제 (다이얼로그에서 새 이름 확정) */}
                    {isMine && !isRenaming && (
                      <button
                        className="std-list-action-btn"
                        title="복제"
                        onClick={(e) => { e.stopPropagation(); openDuplicate(c) }}
                      >⎘</button>
                    )}
                  </div>
                  {!isRenaming && c.description && (
                    <div className="std-list-desc">
                      <HighlightText text={c.description} query={compQuery} />
                    </div>
                  )}
                  {!isRenaming && (c.updated_at || c.token_count != null) && (
                    <div className="std-list-meta">
                      {c.updated_at && <span>{String(c.updated_at).slice(0, 10)}</span>}
                      <TokenBadge count={c.token_count} />
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
        </section>

        {/* ───── 조합 재료 그룹: 새 조합에 넣을 후보 (Composite + Semantic) ─────
             내부는 [Composite 결합] / [Semantic Views] 두 subsection 이 세로로 나란히.
             materialsPct 로 50/50 (기본) 비율 + .composite-row-splitter 로 사용자 조정 가능. */}
        <section className="cv-section cv-section-materials" ref={materialsRef}>
        <div className="cv-group-header">⊕ 조합 재료</div>

        {/* ── Composite 결합 (기존 Composite 를 새 Composite 의 재료로) ── */}
        {availableComposites.length > 0 && (
          <div className="cv-subsection" style={{ flex: materialsPct, minHeight: 0 }}>
            <div className="cv-subsection-header">
              <span>Composite 결합</span>
              <button
                className="btn-xs cv-owner-toggle"
                onClick={() => setShowOthersRefs(v => !v)}
                title={showOthersRefs
                  ? '클릭하면 내 Composite 만 표시'
                  : '클릭하면 다른 사람의 Composite 도 함께 표시'}
              >{showOthersRefs ? '👥 전체' : '🙋 내것만'}</button>
              <span className="cv-count">
                {selectedCompositeRefs.size}/{availableComposites.length}
              </span>
            </div>
            {/* Composite 결합 검색 bar */}
            <div style={{ display: 'flex', padding: '4px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)' }}>
              <SearchInput
                value={refQuery}
                onChange={setRefQuery}
                placeholder="결합 후보 검색"
                total={showOthersRefs
                  ? availableComposites.length
                  : availableComposites.filter(c => c.is_mine !== false).length}
                matched={filteredAvailableComposites.length}
                hotkey="j"
                compact
              />
            </div>
            <div className="std-list">
              {availableComposites.length > 0 && filteredAvailableComposites.length === 0 && (
                <div className="std-list-empty">조건에 맞는 항목 없음</div>
              )}
              {filteredAvailableComposites.map(c => {
                const ownerName = c.owner_name || c.owner_user_id || ''
                const isMine = c.is_mine !== false
                // 리뷰 #1: 복합 키 (owner::name) — 같은 이름 다른 owner CV 도 독립 토글
                const cKey = itemKey(c)
                return (
                  <label key={cKey} className={`std-list-item with-check ${selectedCompositeRefs.has(cKey) ? 'selected' : ''}`}>
                    <input type="checkbox"
                      checked={selectedCompositeRefs.has(cKey)}
                      onChange={() => toggleCompositeRef(cKey)}
                    />
                    <div className="std-list-body">
                      <div className="std-list-name-row">
                        <span className="std-list-name">
                          <HighlightText text={c.name} query={refQuery} />
                        </span>
                        {ownerName && (
                          <span className="std-list-owner">
                            <HighlightText text={ownerName} query={refQuery} />
                            {isMine && <span className="std-list-user-badge">나</span>}
                          </span>
                        )}
                        <span style={{ fontSize: 9, color: 'var(--accent)', opacity: 0.7, flexShrink: 0 }}>C</span>
                        <button
                          type="button"
                          className="btn-xs cv-preview-btn"
                          title="내용 미리보기 (조합 전 확인)"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPreview(c, 'composite') }}
                        >👁</button>
                      </div>
                      {c.description && (
                        <div className="std-list-desc">
                          <HighlightText text={c.description} query={refQuery} />
                        </div>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
          </div>
        )}

        {/* row-splitter — 두 subsection 모두 표시될 때만 노출 (드래그로 비율 조정) */}
        {availableComposites.length > 0 && (
          <div
            className="composite-row-splitter"
            onMouseDown={onMaterialsSplitterMouseDown}
            title="드래그로 위/아래 영역 크기 조정"
          />
        )}

        {/* ── Semantic Views (소스 선택) ── */}
        <div
          className="cv-subsection"
          style={{
            flex: availableComposites.length > 0 ? (100 - materialsPct) : 1,
            minHeight: 0,
          }}
        >
        <div className="cv-subsection-header">
          <span>Semantic Views</span>
          <button
            className="btn-xs cv-owner-toggle"
            onClick={() => setShowOthersMetrics(v => !v)}
            title={showOthersMetrics
              ? '클릭하면 내 Semantic View 만 표시'
              : '클릭하면 다른 사람의 Semantic View 도 함께 표시'}
          >{showOthersMetrics ? '👥 전체' : '🙋 내것만'}</button>
          <span className="cv-count">
            {selectedMetrics.size}/{savedViews.length}
          </span>
        </div>

        {/* Semantic Views 검색 bar */}
        <div style={{ display: 'flex', padding: '4px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)' }}>
          <SearchInput
            value={metricQuery}
            onChange={setMetricQuery}
            placeholder="Semantic View 검색"
            total={showOthersMetrics
              ? savedViews.length
              : savedViews.filter(v => v.is_mine !== false).length}
            matched={filteredSavedViews.length}
            hotkey="i"
            compact
          />
        </div>

        <div className="std-list" style={{ minHeight: 0 }}>
          {loadingViews ? (
            <div className="std-list-empty">로딩 중...</div>
          ) : savedViews.length === 0 ? (
            <div className="std-list-empty">
              저장된 뷰가 없습니다.<br/>Semantic View 모드에서 뷰를 생성/저장하세요.
            </div>
          ) : filteredSavedViews.length === 0 ? (
            <div className="std-list-empty">조건에 맞는 뷰 없음</div>
          ) : (
            filteredSavedViews.map(v => {
              const ownerName = v.owner_name || v.owner_user_id || ''
              const isMine = v.is_mine !== false
              // 리뷰 #1: 복합 키 (owner::name) — 같은 이름 다른 owner SV 도 독립 토글
              const vKey = itemKey(v)
              return (
                <label key={vKey} className={`std-list-item with-check ${selectedMetrics.has(vKey) ? 'selected' : ''}`}>
                  <input type="checkbox"
                    checked={selectedMetrics.has(vKey)}
                    onChange={() => toggleMetric(vKey)}
                  />
                  <div className="std-list-body">
                    <div className="std-list-name-row">
                      <span className="std-list-name">
                        <HighlightText text={v.name} query={metricQuery} />
                      </span>
                      {ownerName && (
                        <span className="std-list-owner">
                          <HighlightText text={ownerName} query={metricQuery} />
                          {isMine && <span className="std-list-user-badge">나</span>}
                        </span>
                      )}
                      <span style={{ fontSize: 9, color: 'var(--text-secondary)', opacity: 0.7, flexShrink: 0 }}>S</span>
                      <button
                        type="button"
                        className="btn-xs cv-preview-btn"
                        title="내용 미리보기 (조합 전 확인)"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); openPreview(v, 'semantic') }}
                      >👁</button>
                    </div>
                    {v.description && (
                      <div className="std-list-desc">
                        <HighlightText text={v.description} query={metricQuery} />
                      </div>
                    )}
                  </div>
                </label>
              )
            })
          )}
        </div>
        </div>{/* end Semantic Views cv-subsection */}
        </section>{/* end cv-section-materials */}
      </div>

      {/* ── 2열: Composite View YAML (CodeMirror) ──────────────────────── */}
      <div style={{ flex: editorPct, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* 저장 바 — Semantic View GenerateBar 와 동일한 패턴 (뷰 이름 + 버튼, 설명) */}
        <div className="composite-save-bar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72 }}>
              뷰 이름
            </label>
            <input
              className="field field-name"
              type="text"
              placeholder="my_composite_view"
              value={compositeName}
              onChange={(e) => setCompositeName(e.target.value)}
            />
            <div className="btn-actions">
              <button className="btn-secondary" onClick={handleSaveComposite}
                disabled={savingComposite || !compositeName.trim() || !yaml_.trim()}>
                {savingComposite ? '저장 중...' : '저장'}
              </button>
              {selectedComposite && selectedCompositeIsMine && (
                <button className="btn-danger" onClick={handleDeleteComposite} title="삭제">삭제</button>
              )}
              {/* 조합 버튼 — 저장 액션 옆 (KBNL-26) */}
              <span className="cv-actions-sep" />
              <button
                className="btn-secondary"
                onClick={() => handleCombine('simple')}
                disabled={loading || (selectedMetrics.size === 0 && selectedCompositeRefs.size === 0)}
                title="선택한 뷰를 단순 병합 (LLM 미사용)"
              >
                {loading && combineMode === 'simple'
                  ? (<><span className="cv-inline-spinner" />조합 중...</>)
                  : `단순 조합 (S:${selectedMetrics.size} C:${selectedCompositeRefs.size})`}
              </button>
              <button
                className={`btn-secondary ${showAiPanel ? 'is-active' : ''}`}
                onClick={() => setShowAiPanel(s => !s)}
                disabled={loading || (selectedMetrics.size === 0 && selectedCompositeRefs.size === 0)}
                title="LLM 으로 사용자 의도 반영 조합"
              >AI 조합 {showAiPanel ? '▴' : '▾'}</button>
            </div>
          </div>
          {/* KBNL-124: 설명 — 기본 1줄, ▾ 토글 시 펼침 (워크벤치와 동일 패턴) */}
          <div style={{ display: 'flex', gap: 8, alignItems: descExpanded ? 'flex-start' : 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72, paddingTop: descExpanded ? 4 : 0 }}>
              설명
            </label>
            <textarea
              className="field field-desc"
              value={compositeDesc}
              onChange={e => setCompositeDesc(e.target.value)}
              placeholder="이 Composite View 에 대한 설명 (선택)"
              rows={descExpanded ? 3 : 1}
              style={descExpanded
                ? { height: 80 }
                : { height: 32, minHeight: 'auto', resize: 'none', overflow: 'hidden', whiteSpace: 'nowrap' }}
            />
            <button
              type="button"
              className="btn-xs"
              onClick={() => setDescExpanded(v => !v)}
              title={descExpanded ? '설명 영역 접기' : '설명 영역 펼치기'}
              style={{ fontSize: 11, padding: '0 6px', flexShrink: 0 }}
            >{descExpanded ? '▴' : '▾'}</button>
          </div>
          {/* AI 조합 프롬프트 패널 — 토글 시 펼침 */}
          {showAiPanel && (
            <div className="cv-ai-panel">
              {/* KBNL-139: 공간 절약 — 레이블 줄에 닫기/실행 버튼을 함께 배치 */}
              <div className="cv-ai-panel-head" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label className="cv-ai-label" style={{ margin: 0 }}>AI 조합 프롬프트</label>
                <span style={{ flex: 1 }} />
                <button className="btn-xs" onClick={() => setShowAiPanel(false)}>닫기</button>
                <button
                  className="btn-primary btn-xs"
                  onClick={() => handleCombine('ai')}
                  disabled={(selectedMetrics.size === 0 && selectedCompositeRefs.size === 0)
                    || aiJobs.some(j => j.status === 'running')}
                  title="LLM 으로 백그라운드 조합 — 프롬프트 비우면 기본 지시문 사용. 다른 컴포지트로 가서 또 실행 가능(병렬)"
                >
                  {aiJobs.some(j => j.status === 'running')
                    ? (<><span className="cv-inline-spinner" />AI 조합 진행 중...</>)
                    : 'AI 조합 실행 (백그라운드)'}
                </button>
              </div>
              <textarea
                className="cv-ai-textarea"
                value={aiPrompt}
                onChange={e => setAiPrompt(e.target.value)}
                placeholder="예) 매출 측정값 중심으로 묶고, 조직 차원만 살려서 하나의 분석용 view 로 만들어줘"
                rows={3}
              />
            </div>
          )}
          {/* KBNL-139/140: AI 조합 백그라운드 잡 배너 (현재 컴포지트).
              완료(done)는 자동 적용하지 않고 [결과 적용] 버튼으로 사용자가 반영(편집 보호). */}
          {aiJobs.map(j => (
            <div key={j.job_id} className="cv-ai-job-banner" style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              margin: '6px 0 0', padding: '6px 10px', fontSize: 12.5, borderRadius: 6,
              border: '1px solid ' + (j.status === 'error' ? '#c0392b' : j.status === 'interrupted' ? '#f59e0b' : j.status === 'done' ? '#16a34a' : '#3b82f6'),
              background: j.status === 'error' ? 'rgba(192,57,43,0.08)' : j.status === 'interrupted' ? 'rgba(245,158,11,0.10)' : j.status === 'done' ? 'rgba(22,163,74,0.08)' : 'rgba(59,130,246,0.08)',
              color: j.status === 'error' ? '#c0392b' : j.status === 'interrupted' ? '#b45309' : j.status === 'done' ? '#15803d' : '#1d4ed8',
            }}>
              {j.status === 'running' && (<><span className="cv-inline-spinner" />AI 조합 진행 중… (백그라운드 — 다른 작업 가능)</>)}
              {j.status === 'done' && (<>✓ AI 조합 완료 — 결과를 적용하시겠어요? (현재 편집 중인 내용은 덮어써집니다)</>)}
              {j.status === 'interrupted' && (<>⚠️ AI 조합이 중단된 것으로 보입니다 (서버 재시작/타임아웃).</>)}
              {j.status === 'error' && (<>✗ AI 조합 실패: {(j.error || '').slice(0, 160)}</>)}
              {/* KBNL-141: 진행 중 강제 중단 */}
              {j.status === 'running' && (
                <button className="btn-xs" style={{ marginLeft: 'auto' }}
                  title="진행 중인 AI 조합 중단 (결과 폐기)"
                  onClick={() => cancelAiJob(j)}
                >■ 중단</button>
              )}
              {j.status === 'done' && (
                <button className="btn-xs btn-primary" style={{ marginLeft: 'auto' }}
                  onClick={() => applyAiJob(j)}
                >결과 적용</button>
              )}
              {j.status !== 'running' && (
                <button className="btn-xs" style={j.status === 'done' ? undefined : { marginLeft: 'auto' }}
                  onClick={() => { appliedJobsRef.current.add(j.job_id); setAiJobs(prev => prev.filter(x => x.job_id !== j.job_id)); api.wbJobsCollect(j.job_id).catch(() => {}) }}
                >닫기</button>
              )}
            </div>
          ))}
        </div>

        {/* KBNL-140: 자식 SV 토큰 줄은 우측 '조합 결과' 카드(뷰별 tok 칩)로 통합 — 여기선 제거 */}

        {/* 에디터 헤더 */}
        <div className="composite-editor-header">
          <span style={{ fontWeight: 600, fontSize: 13 }}>Composite View YAML</span>
          {/* KBNL-69: 본문 헤더 토큰 배지 — 마지막 저장 기준 (자식 chip 줄은 위에 별도) */}
          {selectedComposite && (
            <TokenBadge
              count={compositeTokenCount}
              variant="strong"
              title="이 Composite View 의 저장된 전체 토큰 수 — 편집 후 저장 시 갱신"
            />
          )}
        </div>
        {/* KBNL-140: 버튼은 제목과 별도 둘째 줄(툴바)로 — 좁은 폭에서 줄바꿈/잘림 방지 */}
        <div className="composite-editor-toolbar">
          <input ref={fileRef} type="file" accept=".yaml,.yml" style={{ display: 'none' }}
                 onChange={handleLoadFile} />
          <button className="btn-xs" onClick={() => fileRef.current?.click()}
            title="로컬 YAML 파일(.yaml/.yml)을 에디터로 불러오기">📂 파일 열기</button>
          <button className="btn-xs" onClick={handleLoadSample}
            title="예제 Composite View YAML 을 에디터에 불러옵니다 (형식 참고용)">🧪 예제</button>
          <button className="btn-xs" onClick={handleAssemble} disabled={loading || !yaml_.trim()}
            title="에디터의 YAML 을 다시 조립해 오른쪽 '조합 결과' 를 갱신합니다 (YAML 을 직접 수정한 뒤 사용)">🔄 결과 갱신</button>
          {/* 다운로드 + GCS Export (KBNL-27) */}
          <button
            className="btn-xs"
            onClick={() => downloadText(ensureExtension(compositeName || 'composite_view', 'yaml'), yaml_, 'text/yaml')}
            disabled={!yaml_.trim()}
            title="현재 Composite YAML 을 로컬로 다운로드"
          >⤓ 다운로드</button>
          <button
            className="btn-xs"
            onClick={() => setShowImport(true)}
            title="GCS 에서 Composite YAML 불러오기 (에디터에 채움)"
          >☁ GCS Import</button>
          <button
            className="btn-xs"
            onClick={() => setShowExport(true)}
            disabled={!yaml_.trim()}
            title="GCS 의 특정 위치로 export"
          >☁ GCS Export</button>
          {/* KBNL-74 후속: CV 전체 기준 "🔍 유사 SV 찾기" 버튼은 제거 — 의미가 모호하고
              실제 검색 결과도 거의 0건. 대신 조립 결과 영역의 각 sub-SV 카드에서
              "🔍 유사 SV 찾기" 버튼을 통해 sub-SV 별로 검색한다. */}
        </div>
        {/* KBNL-141: flex-column + min-height:0 + overflow:hidden — 내부 cm-scroller 가
            스크롤하고 상태바(Ln/Col)가 항상 하단에 고정되도록 (바깥 래퍼가 스크롤돼
            상태바가 잘려 안 보이던 문제 수정). */}
        <div className="editor-wrap" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {yaml_ ? (
            <CodeMirror
              value={yaml_}
              height="100%"
              theme={editorTheme || oneDark}
              extensions={cmExtensions}
              onChange={setYaml}
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
                autocompletion: false,
              }}
            />
          ) : (
            <div className="placeholder">
              좌측에서 뷰를 선택하고 [단순 조합] 또는 [AI 조합]을 클릭하거나,{'\n'}
              YAML 파일을 불러오세요.
            </div>
          )}
        </div>
      </div>

      {/* ── 스플리터 ───────────────────────────────────────────────────── */}
      <div
        ref={splitterRef}
        className="composite-splitter"
        onMouseDown={onSplitterMouseDown}
      />

      {/* ── 3열: 조합 결과 ─────────────────────────────────────────────── */}
      <div style={{ flex: 100 - editorPct, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* KBNL-140: 패널 제목 — '미리보기' 대신 '조합 결과'로 직관화 */}
        <div className="composite-editor-header">
          <span style={{ fontWeight: 600, fontSize: 13 }}>조합 결과</span>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>빌드 순서 · 뷰별 필드/소스 구성</span>
          <span style={{ flex: 1 }} />
          {result?.views?.length > 0 && (
            <span className="composite-view-count">{result.views.length}개 뷰</span>
          )}
        </div>
        {!result ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flex: 1, color: 'var(--text-secondary)', fontSize: 13, padding: 20, textAlign: 'center' }}>
            좌측에서 뷰를 선택하고 [단순 조합] 또는 [AI 조합]을 클릭하면{'\n'}여기에 조합 결과가 나타납니다.
          </div>
        ) : (
          <>
            {/* 조립 순서 — chip 클릭 시 해당 카드로 스크롤 */}
            {result.dependency_order?.length > 0 && (
              <div className="composite-dep-row">
                <div className="composite-section-label">빌드 순서</div>
                <div className="composite-dep-chips">
                  {result.dependency_order.map((name, i) => (
                    <span key={name} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span
                        className="composite-dep-chip"
                        onClick={() => scrollToView(name)}
                        title={`'${name}' 카드로 이동`}
                      >
                        {name}
                      </span>
                      {i < result.dependency_order.length - 1 && (
                        <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>→</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 카드 리스트 — 결합된 각 view 의 구성 요약 */}
            <div className="composite-summary-list">
              {result.views?.map(v => {
                const src = sourceMap[v.name]
                return (
                  <div
                    key={v.name}
                    data-view-name={v.name}
                    className="composite-summary-card"
                  >
                    <div className="composite-summary-header">
                      {src && (
                        <span className={`source-origin-badge origin-${src.origin}`}>
                          {src.origin === 'sql_converter' ? 'SQL'
                            : src.origin === 'composite' ? 'CV'
                            : src.origin === 'ai_combine' ? 'AI'
                            : 'Gen'}
                        </span>
                      )}
                      <span className="composite-summary-name">{v.name}</span>
                      <span className="composite-summary-fieldcount">
                        {v.fields?.length || 0} fields
                      </span>
                      {/* KBNL-140: 이 뷰(자식 SV)의 토큰 수 — 기존 '자식 SV 토큰' 줄을 카드로 통합 */}
                      {tokenByName[v.name] != null && (
                        <span className="composite-summary-tok"
                          title={`${tokenByName[v.name].toLocaleString()} 토큰 (저장 시 측정)`}>
                          {tokenByName[v.name] >= 1000
                            ? `${(tokenByName[v.name] / 1000).toFixed(1).replace(/\.0$/, '')}K`
                            : tokenByName[v.name]} tok
                        </span>
                      )}
                      {src?.origin === 'sql_converter' && src.sql_text && (
                        <button
                          className="btn-xs"
                          onClick={() => handleViewSql(v.name)}
                          style={{ marginLeft: 'auto', fontSize: 10, padding: '1px 6px' }}
                        >
                          원본 SQL
                        </button>
                      )}
                      {/* KBNL-74 후속: sub-SV 별 유사 SV 찾기 — name + description + 소스 테이블/뷰
                          명까지 query 에 포함해 hybrid search 매칭률을 올린다. "원본 SQL" 버튼이
                          없는 카드에서는 marginLeft:auto 로 우측 정렬, 있는 카드에서는 그 옆에 붙음. */}
                      <button
                        className="btn-xs"
                        onClick={() => {
                          const tableNames = (v.sources || [])
                            .map(s => s.table || s.semantic_view || s.derived_table || '')
                            .filter(Boolean)
                          const query = [v.name, v.description || '', ...tableNames]
                            .filter(Boolean).join(' ')
                          setRecommendForView({ name: v.name, query })
                        }}
                        style={{
                          marginLeft: src?.origin === 'sql_converter' && src.sql_text ? 4 : 'auto',
                          fontSize: 10, padding: '1px 6px',
                        }}
                        title={`'${v.name}' 와 유사한 Semantic View 찾기`}
                      >
                        🔍 유사 SV 찾기
                      </button>
                    </div>

                    {v.description && (
                      <div className="composite-summary-desc">{v.description}</div>
                    )}

                    {/* 소스: 테이블 / semantic_view 참조 / derived_table 구분 */}
                    {v.sources?.length > 0 && (
                      <div className="composite-summary-section">
                        <div className="composite-section-label">소스</div>
                        <div className="composite-summary-sources">
                          {v.sources.map((s, i) => {
                            const kind = s.semantic_view ? 'view'
                              : s.derived_table ? 'derived' : 'table'
                            const label = s.table || s.semantic_view
                              || (s.derived_table ? '(derived SQL)' : '(unknown)')
                            const onClick = kind === 'view'
                              ? () => scrollToView(s.semantic_view) : undefined
                            return (
                              <span
                                key={i}
                                className={`composite-source-chip kind-${kind} ${onClick ? 'clickable' : ''}`}
                                onClick={onClick}
                                title={kind === 'view' ? `'${s.semantic_view}' 카드로 이동` : undefined}
                              >
                                <span className="composite-source-alias">{s.name}</span>
                                <span className="composite-source-sep">←</span>
                                <span className="composite-source-target">{label}</span>
                              </span>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* 조인 — type / source ⇋ target / on condition */}
                    {v.joins?.length > 0 && (
                      <div className="composite-summary-section">
                        <div className="composite-section-label">조인</div>
                        <div className="composite-summary-joins">
                          {v.joins.map((j, i) => (
                            <div key={i} className="composite-join-row">
                              <span className={`composite-join-type type-${(j.type || 'inner').toLowerCase()}`}>
                                {j.type || 'inner'}
                              </span>
                              <span className="composite-join-pair">
                                <code>{j.source}</code>
                                <span className="composite-join-arrow">⇋</span>
                                <code>{j.target}</code>
                              </span>
                              {j.condition && (
                                <code className="composite-join-condition">{j.condition}</code>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 의존 view */}
                    {v.dependencies?.length > 0 && (
                      <div className="composite-summary-section">
                        <div className="composite-section-label">의존</div>
                        <div className="composite-summary-deps">
                          {v.dependencies.map(dep => (
                            <span
                              key={dep}
                              className="composite-dep-chip clickable"
                              onClick={() => scrollToView(dep)}
                              title={`'${dep}' 카드로 이동`}
                            >
                              {dep}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* ── 원본 SQL 보기 모달 ──────────────────────────────────────────── */}
      {sqlModal && (
        <div className="sql-modal-overlay" onClick={() => setSqlModal(null)}>
          <div className="sql-modal" onClick={e => e.stopPropagation()}>
            <div className="sql-modal-header">
              <span style={{ fontWeight: 600 }}>원본 SQL: {sqlModal.name}</span>
              <button className="btn-xs" onClick={() => {
                copyToClipboard(sqlModal.sql_text).catch(() => showToast('error', '클립보드 복사 실패'))
                showToast('success', 'SQL 복사 완료')
              }}>복사</button>
              <button className="btn-xs" onClick={() => setSqlModal(null)}>닫기</button>
            </div>
            <pre className="sql-modal-body">{sqlModal.sql_text}</pre>
          </div>
        </div>
      )}

      {/* Composite View 복제 다이얼로그 (KBNL-65) */}
      <DuplicateDialog
        open={!!duplicateDialog}
        defaultName={duplicateDialog?.defaultName || ''}
        busy={duplicating}
        title="Composite View 복제"
        hint="같은 본문(YAML + selected_views + sources) 으로 새 Composite View 가 만들어집니다."
        onConfirm={handleDuplicate}
        onCancel={() => { if (!duplicating) setDuplicateDialog(null) }}
      />

      {/* GCS Export 다이얼로그 (KBNL-27) */}
      <GcsExportDialog
        open={showExport}
        onClose={() => setShowExport(false)}
        defaultPath={storageSettings?.gcs_path || 'gs://'}
        defaultFilename={ensureExtension(compositeName || 'composite_view', 'yaml')}
        defaultExt="yaml"
        content={yaml_ || ''}
        contentType="text/yaml"
        showToast={showToast}
        title="Composite View YAML 을 GCS 로 Export"
      />

      {/* KBNL-140: GCS Import — Composite YAML 을 GCS 에서 불러와 에디터에 채움 */}
      <GcsImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        defaultPath={storageSettings?.gcs_path || 'gs://'}
        onImported={(content) => { setYaml(content); setResult(null); setShowImport(false); showToast('success', 'GCS 에서 불러오기 완료') }}
        showToast={showToast}
        title="GCS 에서 Composite View YAML 불러오기"
      />

      {/* KBNL-74 후속: 카드별 sub-SV 기준 유사 SV 추천 모달.
          recommendForView 가 set 되면 그 sub-SV 의 name + description + 소스 테이블 명을
          query_text 로 모달이 열리고, 닫으면 null 로 되돌아간다 (single-select 네비).
          CV → SV 탭 cross-tab 네비는 App.jsx 콜백이 없어 toast 안내만. */}
      <SvRecommendDialog
        open={!!recommendForView}
        onClose={() => setRecommendForView(null)}
        mode="query"
        initialQueryText={recommendForView?.query || ''}
        storageSettings={storageSettings}
        showToast={showToast}
        editorTheme={editorTheme}
        onSelect={(c) => {
          if (c?.name) {
            showToast?.('info', `유사 SV 찾기 결과: ${c.name} — Semantic View 탭에서 열어 확인하세요.`)
          }
        }}
        title={`'${recommendForView?.name || ''}' 와(과) 유사한 Semantic View`}
      />

      {/* KBNL-141: 조합 재료 내용 미리보기 (읽기 전용) */}
      {previewItem && (
        <div className="cv-preview-backdrop" onClick={() => setPreviewItem(null)}>
          <div className="cv-preview-panel" onClick={e => e.stopPropagation()}>
            <div className="cv-preview-head">
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <span className="cv-preview-kind">{previewItem.kind === 'composite' ? 'CV' : 'SV'}</span>
                <span className="cv-preview-title" title={previewItem.name}>{previewItem.name}</span>
                {previewItem.ownerName && (
                  <span className="std-list-owner">{previewItem.ownerName}</span>
                )}
              </span>
              <button className="btn-xs" onClick={() => setPreviewItem(null)}>닫기</button>
            </div>
            {previewItem.description && (
              <div className="cv-preview-desc">{previewItem.description}</div>
            )}
            <div className="cv-preview-body">
              {previewItem.loading ? (
                <div className="std-list-empty">불러오는 중…</div>
              ) : (
                <CodeMirror
                  value={previewItem.content || ''}
                  height="100%"
                  theme={editorTheme || oneDark}
                  extensions={[yaml()]}
                  basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: false, autocompletion: false }}
                  editable={false}
                  readOnly={true}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


/* ── 여러 Composite YAML을 하나로 병합 ───────────────────────────────────
 *
 * 리뷰 #3: 두 개 이상의 CV 가 같은 sub-SV 를 포함하면 단순 concatenate 시
 * assembler 가 'semantic_view 이름 중복' fatal raise. 여기서 name 기준 dedup
 * (첫 정의 유지). 호출자는 opts.onDuplicate 콜백으로 어떤 이름이 제거됐는지
 * 받아서 toast 등으로 사용자에게 알릴 수 있다.
 *
 * 단순 dedup 의 한계: 같은 이름이지만 서로 다른 owner 의 정의는 어느 쪽이
 * 살아남는지 호출 순서에 의존. 협업 모드에서 의도된 owner 선택이 필요하면
 * mergedYamlParts 의 push 순서를 의도적으로 정해야 함.
 */
function mergeCompositeYamls(yamlParts, opts = {}) {
  const { onDuplicate } = opts
  const allDocs = []
  const seenNames = new Set()

  // 단일 doc 에서 'semantic_view: name:' 추출. 정규식 fragility 는 알지만
  // 기존 extractViewNamesFromYaml 와 동일 휴리스틱 — YAML parser 도입은 별도 작업.
  const extractName = (doc) => {
    const m = doc.match(/^\s*name:\s*(.+)$/m)
    return m ? m[1].trim().replace(/^["']|["']$/g, '') : null
  }
  const tryAdd = (doc) => {
    const name = extractName(doc)
    if (name && seenNames.has(name)) {
      if (onDuplicate) onDuplicate(name)
      return                              // dedup
    }
    if (name) seenNames.add(name)
    allDocs.push(doc)
  }

  for (const part of yamlParts) {
    const trimmed = part.trim()
    // --- 로 분리된 멀티 도큐먼트 → 각각 추가
    const docs = trimmed.split(/^---$/m).map(d => d.trim()).filter(Boolean)
    for (const doc of docs) {
      if (/^semantic_view\s*:/m.test(doc)) {
        tryAdd(doc)
      } else if (/^semantic_views\s*:/m.test(doc)) {
        // 하위 호환: semantic_views 리스트에서 각 - semantic_view: 블록 추출
        const items = doc.split(/^(?=\s*-\s*semantic_view\s*:)/m).filter(Boolean)
        for (const item of items) {
          const cleaned = item.replace(/^semantic_views\s*:\s*/m, '').trim()
          if (!cleaned) continue
          // - semantic_view: 에서 앞의 - 와 들여쓰기 제거
          const unwrapped = cleaned.replace(/^-\s*/, '')
          if (/^semantic_view\s*:/m.test(unwrapped)) {
            tryAdd(unwrapped)
          }
        }
      }
    }
  }

  if (allDocs.length === 0) {
    return yamlParts.join('\n---\n')
  }

  return allDocs.join('\n---\n') + '\n'
}
